// Copyright 2017 Sorama B.V.

import { BindingEngine, Disposable, autoinject, observable, transient } from "aurelia-framework";
import { WorkflowModalCatalog } from "catalogs/WorkflowModalCatalog";
import {
    BarBottomLocation,
    BarData,
    BarDataDisplayOptions,
    BarTextLocation,
    Boundary,
    LineData,
    PlotData,
} from "controls/plot/graph-plot/GraphPlot";
import { UnitValueToStringValueConverter } from "converters/UnitValueToStringValueConverter";
import { AxisKind, FrequencyAxisKinds, LinearFrequencyAxisKind } from "datatypes/SettingsKinds/AxisKinds";
import { FrequencyBandKind, FrequencyBandKinds } from "datatypes/SettingsKinds/FrequencyBandKinds";
import { Range as IRange, Point2D, Spectrum, StreamSpectrum } from "datatypes/SoramaLocalApi";
import { BandValue, LinearFrequencyBand, OctaveBands } from "libraries/Math/OctaveBands";
import {
    IWorkflowModuleInstance,
    IWorkflowModuleLifetime,
    IWorkflowModuleModalInstance,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { WorkflowPropertyBindingMode } from "libraries/WorkflowFramework/Interfaces/IWorkflowPropertyMetadata";
import { userSetting } from "libraries/WorkflowFramework/UserSettingsAttribute";
import { workflowProperty } from "libraries/WorkflowFramework/WorkflowProperties/WorkflowPropertyAttribute";
import { SettingsModalState } from "modules/modals/SettingsModal";
import { AxisKindParser } from "parsers/AxisKindParser";
import { WeightedPressureUnitParser } from "parsers/WeightedPressureUnitParser";
import { FrequencySelectionService } from "services/FrequencySelection/FrequencySelectionService";
import { HardwareSampleRate } from "services/Hardware/HardwareSamplerRate";
import { IMeasurementPreviewService } from "services/Measurement/IMeasurementPreviewService";
import { Frequency } from "units/Frequency";
import { Pressure } from "units/Pressure";
import { Unit, UnitValue, WeightedUnit } from "units/Unit";
import { UnitSpectrum } from "units/UnitSpectrum";
import { WeightedSpectrum } from "units/WeightedSpectrum";
import { ZWeighting } from "units/Weighting";

@transient()
@autoinject
export class SpectrumModule implements IWorkflowModuleLifetime {
    //#region Constants

    // Results from the lower frequency of the second octave band (44-88 Hz) which is the first band that does not suffer from DC leakage when streaming
    public minimumDisplayedXUnitValue = new UnitValue(OctaveBands.OctaveBandFrequencies[1].lower, Frequency.hertz);

    // Get the nyquist frequency given the default sample rate
    public maximumDisplayedXUnitValue = new UnitValue(HardwareSampleRate.defaultCAMSampleRate / 2, Frequency.hertz);

    public static UnitValueToStringValueConverter = new UnitValueToStringValueConverter();
    //#endregion

    //#region View Fields
    public spectra: PlotData[];

    public displayYAxisUnit: Unit = Pressure.dbSPL;

    public displayXAxisUnit: Unit = Frequency.hertz;
    //#endregion

    //#region State Fields
    private viewActive: boolean;
    private currentUnitSpectrum: UnitSpectrum;
    private measurementUpdated: boolean;
    private nextUpdatePromise: Promise<unknown>;

    private subscriptions: Disposable[];
    moduleInstance: IWorkflowModuleInstance;
    private streamerSpectrumSettingsModal: IWorkflowModuleModalInstance<SettingsModalState<SpectrumModule>, void>;

    //#endregion

    //#region Workflow Properties
    @observable
    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    @userSetting()
    public isFrequencyRangeLinked: boolean;

    @observable
    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    public frequencyRange: IRange;
    //#endregion

    //#region Settings
    @observable({ changeHandler: "xUnitRangeInputChanged" })
    @userSetting()
    public minXAxis: number = this.minimumDisplayedXUnitValue.value;

    @observable({ changeHandler: "xUnitRangeInputChanged" })
    @userSetting()
    public maxXAxis: number = this.maximumDisplayedXUnitValue.value;

    public frequencyAxisKinds = FrequencyAxisKinds;

    @userSetting() public minYAxis = 0;
    @userSetting() public maxYAxis = 80;

    @observable
    @userSetting({ parser: new AxisKindParser() })
    public selectedFrequencyAxisKind: AxisKind = LinearFrequencyAxisKind;

    @observable
    @userSetting({ parser: new WeightedPressureUnitParser() })
    public selectedUnit: WeightedUnit;

    @observable public selectedFrequencyBandKind: FrequencyBandKind = FrequencyBandKinds.None;

    public availableUnits: WeightedUnit[];
    public frequencyBandKinds: FrequencyBandKind[] = FrequencyBandKinds.All();

    //#endregion

    //#region Constructor and Initialization
    constructor(
        private readonly bindingEngine: BindingEngine,
        private readonly measurementService: IMeasurementPreviewService,
        private readonly frequencySelectionService: FrequencySelectionService
    ) {}

    public activateModule(moduleInstance: IWorkflowModuleInstance): void {
        this.moduleInstance = moduleInstance;

        this.availableUnits = [Pressure.dbSPL, Pressure.dbASPL, Pressure.dbBSPL, Pressure.dbCSPL];
        this.selectedUnit = this.availableUnits[0];

        const streamMeasurementObserver = this.bindingEngine.propertyObserver(
            this.measurementService,
            "isSpectrumAvailable"
        );
        this.subscriptions = [streamMeasurementObserver.subscribe(this.streamActiveChanged)];

        this.viewActive = true;
        this.streamActiveChanged();
    }

    public deactivateModule(): void {
        this.viewActive = false;

        while (this.subscriptions.length > 0) this.subscriptions.pop().dispose();
    }
    //#endregion
    private streamActiveChanged = () => {
        this.measurementUpdated = true;

        if (!this.measurementService.isSpectrumAvailable.available) {
            return;
        }

        if (this.nextUpdatePromise) {
            this.nextUpdatePromise.then(() => this.beginGetSpectrum());
        } else this.nextUpdatePromise = this.beginGetSpectrum();
    };

    private frequencyRangeChanged = (newRange: IRange) => {
        if (this.isFrequencyRangeLinked && newRange) {
            this.minXAxis = newRange.from;
            this.maxXAxis = newRange.to;
        }
    };

    private isFrequencyRangeLinkedChanged(): void {
        // Trigger update
        this.frequencyRangeChanged(this.frequencyRange);
    }

    private xUnitRangeInputChanged = () => {
        this.frequencyRange = <IRange>{
            from: this.minXAxis,
            to: this.maxXAxis,
        };
    };

    private selectedUnitChanged = () => {
        this.displayYAxisUnit = this.selectedUnit;
        this.updateSpectrum();
    };

    private selectedFrequencyBandKindChanged = () => {
        this.updateSpectrum();
    };

    private selectedFrequencyAxisKindChanged = () => {
        this.updateSpectrum();
    };

    //#region Spectrum Methods
    private beginGetSpectrum(): Promise<void> {
        return this.measurementService
            .previewSpectrum()
            .then((x) => this.endGetSpectrum(x))
            .catch(() => {
                return this.exitGetSpectrum();
            });
    }

    private endGetSpectrum(spectrum: StreamSpectrum): Promise<void> | void {
        // Remove DC
        if (spectrum.dataPoints) {
            // Find in the sorted array the frequencies to remove
            const numberOfDcComponents = spectrum.dataPoints.findIndex(
                (x) => x.x > this.minimumDisplayedXUnitValue.toUnit(Frequency.hertz)
            );
            spectrum.dataPoints.splice(0, numberOfDcComponents);
        }

        this.currentUnitSpectrum = UnitSpectrum.FromSpectrum(Frequency.hertz, Pressure.pascal, <Spectrum>{
            data: spectrum.dataPoints,
            properties: {
                timeRange: { from: 0, to: 0 },
            },
        });

        this.updateSpectrum();

        // Request new hologram if needed
        return this.exitGetSpectrum();
    }

    private exitGetSpectrum(): Promise<void> | void {
        if (this.measurementService.isSpectrumAvailable.available && this.viewActive) return this.beginGetSpectrum();
        else this.nextUpdatePromise = null;
    }

    private updateSpectrum(): void {
        if (!this.currentUnitSpectrum) return;

        // Apply weighting
        const weightedSpectrum = WeightedSpectrum.FromUnitSpectrum(new ZWeighting(), this.currentUnitSpectrum);
        weightedSpectrum.weighting = this.selectedUnit.weighting;

        // Convert to db
        const dbWeightedSpectrum = weightedSpectrum.convertToUnits(
            this.selectedFrequencyAxisKind.unit,
            this.displayYAxisUnit
        );

        // Show
        this.spectra = [
            new LineData(
                dbWeightedSpectrum.data,
                dbWeightedSpectrum.xUnit,
                dbWeightedSpectrum.yUnit,
                dbWeightedSpectrum.weighting
            ),
        ];

        // Add octave bands
        if (this.selectedFrequencyBandKind != FrequencyBandKinds.None) {
            // Calculate Octave Bands
            const deltaf = this.currentUnitSpectrum.data[1].x - this.currentUnitSpectrum.data[0].x;

            const spectrumFrequencyBandValues = this.currentUnitSpectrum.data.map(
                (x) => new BandValue(new LinearFrequencyBand(x.x, deltaf), x.y)
            );

            const octaveBandValues = this.selectedFrequencyBandKind.toBands(spectrumFrequencyBandValues);

            const octaveBandUnitSpectrum = new UnitSpectrum(
                this.currentUnitSpectrum.xUnit,
                this.currentUnitSpectrum.yUnit,
                octaveBandValues.map((x) => <Point2D>{ x: x.band.center, y: x.value }),
                this.currentUnitSpectrum.properties.timeRange
            );

            // Apply weighting
            const weightedOctaveBandSpectrum = WeightedSpectrum.FromUnitSpectrum(
                new ZWeighting(),
                octaveBandUnitSpectrum
            );
            weightedOctaveBandSpectrum.weighting = this.selectedUnit.weighting;

            // Convert to db
            const dbWeightedOctaveBandSpectrum = weightedOctaveBandSpectrum.convertToUnits(
                this.selectedFrequencyAxisKind.unit,
                this.displayYAxisUnit
            );

            const boundaryForBar: Boundary[] = octaveBandValues.map(
                (x) => <Boundary>{ lower: x.band.lower, upper: x.band.upper }
            );

            // Show
            this.spectra.unshift(
                new BarData(
                    dbWeightedOctaveBandSpectrum.data,
                    dbWeightedOctaveBandSpectrum.xUnit,
                    dbWeightedOctaveBandSpectrum.yUnit,
                    dbWeightedOctaveBandSpectrum.weighting,
                    <BarDataDisplayOptions>{
                        barMargin: 0.001,
                        barBottomLocation: BarBottomLocation.GraphBottom,
                        barLabel: (point, idx) =>
                            OctaveBands.toHumanReadableBandFrequency(
                                this.displayXAxisUnit.fromUnit(dbWeightedOctaveBandSpectrum.xUnit, point.x)
                            ),
                        barLabelLocation: BarTextLocation.GraphBottom,
                    },
                    boundaryForBar
                )
            );
        }
    }
    //#endregion

    public linkFrequencyRange(link: boolean): void {
        this.isFrequencyRangeLinked = link;
    }

    public openStreamerSpectrumSettingsModal(): void {
        if (this.streamerSpectrumSettingsModal && this.streamerSpectrumSettingsModal.isOpen) {
            this.streamerSpectrumSettingsModal.cancel();
            this.streamerSpectrumSettingsModal = undefined;
            return;
        }
        this.moduleInstance
            .createModal<SettingsModalState<SpectrumModule>, void>(WorkflowModalCatalog.StreamerSpectrumSettingsModal)
            .withState({ belongsTo: this })
            .whenClosed(() => {
                this.streamerSpectrumSettingsModal = undefined;
            })
            .show()
            .then((x) => {
                this.streamerSpectrumSettingsModal = x;
            });
    }
}
